import { useEffect, useState } from 'react';
import Input from '@components/ui/Input';
import Select from '@components/ui/Select';
import Button from '@components/ui/Button';
import Icon from '@components/AppIcon';


const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    doctor: '',
    location: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  const doctorOptions = [
    { value: 'dr-kaushik', label: 'Dr. Kaushik Solanki' },
    { value: 'dr-shrikant', label: 'Dr. Shrikant Dalwadi' },
    { value: 'dr-shaileen', label: 'Dr. Shaileen Parikh' },
    { value: 'dr-amit', label: 'Dr. Amit Patel' },
    { value: 'any-available-doctor', label: 'Any Available Doctor' },
  ];

  const timeSlots = [
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' }
  ];

  const locationOptions = [
    { value: 'westlands', label: 'Westlands Branch' },
    //{ value: 'eastleigh', label: 'Eastleigh Branch' },
    { value: 'nakuru', label: 'Nakuru Branch' }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email';

    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^(\+254|0)[17]\d{8}$/.test(formData.phone.replace(/\s/g, '')))
      newErrors.phone = 'Enter a valid Kenyan phone number';

    if (!formData.date) newErrors.date = 'Appointment date is required';
    else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) newErrors.date = 'Please select a future date';
    }

    if (!formData.time) newErrors.time = 'Appointment time is required';
    if (!formData.location) newErrors.location = 'Please select a location';
    if (!formData.doctor) newErrors.doctor = 'Please select a doctor';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // In your handleSubmit function, update the error handling:
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;
  setIsSubmitting(true);

  try {
    const response = await fetch('/api/book-appointment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(formData),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to send appointment');
    }

    setSubmitSuccess(true);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      doctor: '',
      location: '',
      message: ''
    });
    setErrors({});

  } catch (error) {
    console.error('Form submission error:', error);
    alert(error.message || 'There was an error sending your appointment request.');
  } finally {
    setIsSubmitting(false);
  }
};

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  useEffect(() => {
  const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
  setCsrfToken(token);
}, []);


  if (submitSuccess) {
    return (
      <div className="bg-gradient-to-br from-green-100 via-white to-green-50 shadow-2xl rounded-3xl p-10 max-w-2xl mx-auto text-center border border-success/30">
        <div className="w-20 h-20 bg-success/90 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
          <Icon name="CheckCircle" size={40} color="white" />
        </div>
        <h3 className="text-3xl font-extrabold text-success mb-4 tracking-tight">
          Appointment Booked Successfully!
        </h3>
        <p className="text-lg text-text-secondary mb-8">
          Thank you for booking with EyeU Kenya.<br />
          We've sent a confirmation email with your appointment details.<br />
          Our team will contact you within 24 hours to confirm your appointment.
        </p>
        <Button
          variant="outline"
          onClick={() => setSubmitSuccess(false)}
          iconName="Calendar"
          iconPosition="left"
          className="border-success text-success hover:bg-success/10"
        >
          Book Another Appointment
        </Button>
      </div>
    );
  }

  return (
    <div className="relative py-12 px-4 md:px-0">
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="w-full h-full bg-gradient-to-br from-primary/10 via-white to-primary/5 rounded-3xl blur-xl"></div>
      </div>
      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="bg-white/90 shadow-2xl rounded-3xl border border-primary/20 p-8 md:p-12">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 rounded-full p-4 shadow-md">
                <Icon name="Eye" size={32} className="text-primary" />
              </div>
            </div>
            <h2 className="text-3xl font-extrabold text-primary mb-2 tracking-tight">
              Schedule Your Eye Care Appointment
            </h2>
            <p className="text-lg text-text-secondary">
              Fill in your details below and we'll confirm your appointment within 24 hours
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input label="Full Name" placeholder="Enter your full name" value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} error={errors.fullName} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />
              <Input label="Email Address" type="email" placeholder="your.email@example.com" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} error={errors.email} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />
            </div>

            <Input label="Phone Number" type="tel" placeholder="+254 700 000 000" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} error={errors.phone} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input label="Preferred Date" type="date" min={getTomorrowDate()} value={formData.date} onChange={e => handleInputChange('date', e.target.value)} error={errors.date} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />
              <Select label="Preferred Time" placeholder="Select appointment time" options={timeSlots} value={formData.time} onChange={value => handleInputChange('time', value)} error={errors.time} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />
            </div>

            <Select label="Select Location" placeholder="Choose your nearest branch" options={locationOptions} value={formData.location} onChange={value => handleInputChange('location', value)} error={errors.location} required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />

            <Select label="Select Doctor" placeholder="Choose your preferred doctor" options={doctorOptions} value={formData.doctor} onChange={value => handleInputChange('doctor', value)} error={errors.doctor} searchable required className="bg-white/80 border border-primary/20 rounded-xl shadow-sm" />

            <div>
              <label className="block text-base font-semibold text-primary mb-2">
                Additional Message
                <span className="text-text-secondary font-normal ml-1">(Optional)</span>
              </label>
              <textarea
                placeholder="Tell us about your symptoms or any specific concerns..."
                value={formData.message}
                onChange={e => handleInputChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-primary/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-transparent resize-none bg-white/80 shadow-sm"
              />
            </div>
            <div className="bg-gradient-to-r from-primary/5 via-white to-primary/10 p-6 rounded-2xl border border-primary/10 shadow-md">
              <div className="flex items-start space-x-4">
                <Icon name="Info" size={24} className="text-accent mt-1 flex-shrink-0" />
                <div className="text-base text-text-secondary">
                  <p className="font-semibold text-primary mb-2">Important Information:</p>
                  <ul className="space-y-1">
                    <li>• Appointments are subject to doctor availability</li>
                    <li>• Please arrive 15 minutes before your scheduled time</li>
                    <li>• Bring your ID and any previous medical records</li>
                    <li>• Consultation fee: <span className="font-bold text-primary">Westlands Branch KES 3,500</span>, <span className="font-bold text-primary">Eastleigh and Nakuru Branch KES 1,500</span> (payable at clinic)</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button type="submit" fullWidth loading={isSubmitting} iconName="Calendar" iconPosition="left" className="bg-gradient-to-r from-primary to-primary/80 text-white font-bold rounded-xl shadow-lg hover:from-primary/90 hover:to-primary/70 transition-all">
              {isSubmitting ? 'Booking Appointment...' : 'Book Appointment'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentForm;